type TooltipPosition = 'floating' | 'top' | 'bottom' | 'left' | 'right'
  | 'top-right-aligned' | 'top-left-aligned' | 'top-middle-aligned'
  | 'bottom-right-aligned' | 'bottom-left-aligned' | 'bottom-middle-aligned';

export interface TourStepDef {
  element?: string;
  title: string;
  intro: string;
  position?: TooltipPosition;
}

type TFunction = (key: string) => string;

function isRootPage(pathname: string): boolean {
  const rootPages = new Set([
    '/',
    '/events',
    '/products',
    '/customers',
    '/users',
    '/my-profile',
    '/product-types',
    '/stand-types',
    '/countries',
    '/dashboard',
  ]);
  return rootPages.has(pathname);
}

export function getTourSteps(role: string, t: TFunction, pathname: string = window.location.pathname): TourStepDef[] {
  const shared: TourStepDef[] = [
    {
      title: t('tour.welcome.title'),
      intro: t('tour.welcome.body'),
    },
  ];

  const rootOnly: TourStepDef[] = isRootPage(pathname)
    ? [
        {
          element: '.sidebar',
          title: t('tour.sidebar.title'),
          intro: t('tour.sidebar.body'),
          position: 'right',
        },
        {
          element: '.menu-toggle',
          title: t('tour.menuToggle.title'),
          intro: t('tour.menuToggle.body'),
          position: 'bottom',
        },
        {
          element: '.status-indicator',
          title: t('tour.status.title'),
          intro: t('tour.status.body'),
          position: 'bottom',
        },
        {
          element: '.select-language',
          title: t('tour.language.title'),
          intro: t('tour.language.body'),
          position: 'bottom',
        },
      ]
    : [];

  if (role === 'admin') {
    return [
      ...shared,
      ...rootOnly,
      ...(isRootPage(pathname)
        ? [
            {
              element: '.ant-menu-item [href="/"]',
              title: t('tour.admin.home.title'),
              intro: t('tour.admin.home.body'),
              position: 'right' as const,
            },
            {
              element: '.ant-menu-item [href="/events"]',
              title: t('tour.admin.events.title'),
              intro: t('tour.admin.events.body'),
              position: 'right' as const,
            },
          ]
        : []),
      {
        element: '[data-tour="events-offline-column"]',
        title: t('tour.admin.eventsOffline.title'),
        intro: t('tour.admin.eventsOffline.body'),
        position: 'top',
      },
      {
        element: '[data-tour="event-info"]',
        title: t('tour.admin.eventDetails.title'),
        intro: t('tour.admin.eventDetails.body'),
        position: 'bottom',
      },
      {
        element: '.ant-tabs-nav-list > .ant-tabs-tab:nth-child(1)',
        title: t('tour.admin.eventTabStands.title'),
        intro: t('tour.admin.eventTabStands.body'),
        position: 'bottom',
      },
      {
        element: '.ant-tabs-nav-list > .ant-tabs-tab:nth-child(2)',
        title: t('tour.admin.eventTabOperators.title'),
        intro: t('tour.admin.eventTabOperators.body'),
        position: 'bottom',
      },
      {
        element: '.ant-tabs-nav-list > .ant-tabs-tab:nth-child(3)',
        title: t('tour.admin.eventTabProducts.title'),
        intro: t('tour.admin.eventTabProducts.body'),
        position: 'bottom',
      },
      {
        element: '.ant-tabs-nav-list > .ant-tabs-tab:nth-child(4)',
        title: t('tour.admin.eventTabSectors.title'),
        intro: t('tour.admin.eventTabSectors.body'),
        position: 'bottom',
      },
      {
        element: '[data-tour="event-actions"]',
        title: t('tour.admin.eventActions.title'),
        intro: t('tour.admin.eventActions.body'),
        position: 'top',
      },
      {
        element: '[data-tour="open-delivery-modal"]',
        title: t('tour.admin.deliveryModal.title'),
        intro: t('tour.admin.deliveryModal.body'),
        position: 'top',
      },
      {
        element: '[data-tour="delivery-modal"]',
        title: t('tour.admin.deliveryModalDetails.title'),
        intro: t('tour.admin.deliveryModalDetails.body'),
        position: 'left',
      },
      {
        element: '[data-tour="checklist-table"]',
        title: t('tour.admin.checklist.title'),
        intro: t('tour.admin.checklist.body'),
        position: 'top',
      },
      {
        element: '[data-tour="stand-details-header"]',
        title: t('tour.admin.standDetailsPage.title'),
        intro: t('tour.admin.standDetailsPage.body'),
        position: 'bottom',
      },
      {
        element: '[data-tour="stand-delivery-bills"]',
        title: t('tour.admin.standDeliveryBills.title'),
        intro: t('tour.admin.standDeliveryBills.body'),
        position: 'top',
      },
      {
        element: '[data-tour="stand-redemption-slips"]',
        title: t('tour.admin.standRedemptionSlips.title'),
        intro: t('tour.admin.standRedemptionSlips.body'),
        position: 'top',
      },
      ...(isRootPage(pathname)
        ? [
            {
              element: '.ant-menu-item [href="/products"]',
              title: t('tour.admin.products.title'),
              intro: t('tour.admin.products.body'),
              position: 'right' as const,
            },
          ]
        : []),
      {
        element: '[data-tour="products-page"]',
        title: t('tour.admin.productsPage.title'),
        intro: t('tour.admin.productsPage.body'),
        position: 'top',
      },
      {
        element: '[data-tour="products-drag-hint"]',
        title: t('tour.admin.productsOrdering.title'),
        intro: t('tour.admin.productsOrdering.body'),
        position: 'bottom',
      },
      ...(isRootPage(pathname)
        ? [
            {
              element: '.ant-menu-item [href="/customers"]',
              title: t('tour.admin.customers.title'),
              intro: t('tour.admin.customers.body'),
              position: 'right' as const,
            },
          ]
        : []),
      {
        element: '[data-tour="customer-details-card"]',
        title: t('tour.admin.customerDetails.title'),
        intro: t('tour.admin.customerDetails.body'),
        position: 'bottom',
      },
      {
        element: '[data-tour="customer-tab-service-prices"]',
        title: t('tour.admin.customerServicePrices.title'),
        intro: t('tour.admin.customerServicePrices.body'),
        position: 'bottom',
      },
      ...(isRootPage(pathname)
        ? [
            {
              element: '.ant-menu-item [href="/users"]',
              title: t('tour.admin.users.title'),
              intro: t('tour.admin.users.body'),
              position: 'right' as const,
            },
            {
              element: '.ant-menu-submenu',
              title: t('tour.admin.others.title'),
              intro: t('tour.admin.others.body'),
              position: 'right' as const,
            },
          ]
        : []),
      {
        element: '.status-indicator',
        title: t('tour.admin.offline.title'),
        intro: t('tour.admin.offline.body'),
        position: 'bottom',
      },
      {
        title: t('tour.done.title'),
        intro: t('tour.done.body'),
      },
    ];
  }

  if (role === 'accountant') {
    return [
      ...shared,
      ...rootOnly,
      ...(isRootPage(pathname)
        ? [
            {
              element: '.ant-menu-item [href="/events"]',
              title: t('tour.accountant.events.title'),
              intro: t('tour.accountant.events.body'),
              position: 'right' as const,
            },
          ]
        : []),
      {
        element: '[data-tour="events-offline-column"]',
        title: t('tour.accountant.eventsOffline.title'),
        intro: t('tour.accountant.eventsOffline.body'),
        position: 'top',
      },
      {
        element: '[data-tour="event-info"]',
        title: t('tour.accountant.eventDetails.title'),
        intro: t('tour.accountant.eventDetails.body'),
        position: 'bottom',
      },
      {
        element: '.ant-tabs-nav-list > .ant-tabs-tab:nth-child(1)',
        title: t('tour.accountant.tabStands.title'),
        intro: t('tour.accountant.tabStands.body'),
        position: 'bottom',
      },
      {
        element: '.ant-tabs-nav-list > .ant-tabs-tab:nth-child(2)',
        title: t('tour.accountant.tabOperators.title'),
        intro: t('tour.accountant.tabOperators.body'),
        position: 'bottom',
      },
      {
        element: '.ant-tabs-nav-list > .ant-tabs-tab:nth-child(3)',
        title: t('tour.accountant.tabProducts.title'),
        intro: t('tour.accountant.tabProducts.body'),
        position: 'bottom',
      },
      {
        element: '.ant-tabs-nav-list > .ant-tabs-tab:nth-child(4)',
        title: t('tour.accountant.tabSectors.title'),
        intro: t('tour.accountant.tabSectors.body'),
        position: 'bottom',
      },
      {
        element: '[data-tour="event-actions"]',
        title: t('tour.accountant.eventActions.title'),
        intro: t('tour.accountant.eventActions.body'),
        position: 'top',
      },
      {
        element: '[data-tour="open-delivery-modal"]',
        title: t('tour.accountant.deliveryModal.title'),
        intro: t('tour.accountant.deliveryModal.body'),
        position: 'top',
      },
      {
        element: '[data-tour="delivery-modal-customer"]',
        title: t('tour.accountant.deliveryModalCustomer.title'),
        intro: t('tour.accountant.deliveryModalCustomer.body'),
        position: 'bottom',
      },
      {
        element: '[data-tour="delivery-modal-stand"]',
        title: t('tour.accountant.deliveryModalStand.title'),
        intro: t('tour.accountant.deliveryModalStand.body'),
        position: 'bottom',
      },
      {
        element: '[data-tour="delivery-modal-stand-details"]',
        title: t('tour.accountant.deliveryModalPreview.title'),
        intro: t('tour.accountant.deliveryModalPreview.body'),
        position: 'left',
      },
      {
        element: '[data-tour="checklist-table"]',
        title: t('tour.accountant.checklist.title'),
        intro: t('tour.accountant.checklist.body'),
        position: 'top',
      },
      {
        element: '[data-tour="stand-details-header"]',
        title: t('tour.accountant.standDetailsPage.title'),
        intro: t('tour.accountant.standDetailsPage.body'),
        position: 'bottom',
      },
      {
        element: '[data-tour="stand-delivery-bills"]',
        title: t('tour.accountant.standDeliveryBills.title'),
        intro: t('tour.accountant.standDeliveryBills.body'),
        position: 'top',
      },
      {
        element: '[data-tour="stand-redemption-slips"]',
        title: t('tour.accountant.standRedemptionSlips.title'),
        intro: t('tour.accountant.standRedemptionSlips.body'),
        position: 'top',
      },
      {
        element: '.status-indicator',
        title: t('tour.accountant.offline.title'),
        intro: t('tour.accountant.offline.body'),
        position: 'bottom',
      },
      {
        title: t('tour.done.title'),
        intro: t('tour.done.body'),
      },
    ];
  }

  if (role === 'customer') {
    return [
      ...shared,
      ...rootOnly,
      {
        element: '.ant-menu-item [href="/my-profile"]',
        title: t('tour.customer.profile.title'),
        intro: t('tour.customer.profile.body'),
        position: 'right',
      },
      {
        title: t('tour.customer.orders.title'),
        intro: t('tour.customer.orders.body'),
      },
      {
        title: t('tour.done.title'),
        intro: t('tour.done.body'),
      },
    ];
  }

  return [
    ...shared,
    ...rootOnly,
    {
      title: t('tour.done.title'),
      intro: t('tour.done.body'),
    },
  ];
}
